import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Stack,
  Switch,
  Textarea,
  Typography,
} from '@healthinal/ui';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { getAppointmentDate, getAppointmentTimeFrom } from '../../../api/created/appointment.ts';
import {
  getGetLabOrdersByPatientQueryKey,
  useCreateQuantiferonTest,
  useUpdateQuantiferonTest,
} from '../../../api/generated/lab-order-controller/lab-order-controller.ts';
import {
  AddressContactDto,
  AddressDto,
  AppointmentDto,
  CreateQuantiferonTestDto,
  CreateQuantiferonTestDtoInterpretation,
  LabOrderDtoType,
} from '../../../api/generated/model';
import { queryClient } from '../../../api/queryClient.ts';
import { FormActionButtons } from '../../../components/form/FormActionButtons.tsx';
import { getDefaultValueForDateInput, getDefaultValueForLocalTimeInput } from '../../../utils/date.ts';
import { blobToBase64 } from '../../../utils/file.ts';
import { toLabOrderSamplingContact } from '../../../utils/labOrder.ts';
import { getStringOrUndefined } from '../../../utils/string.ts';
import { DeleteLabOrderButton } from './DeleteLabOrderButton.tsx';
import { LabOrderDetailsForm } from './LabOrderDetailsForm.tsx';
import { ResultInterpretationButtonGroup } from './ResultInterpretationButtonGroup.tsx';

interface FormValues {
  samplingDate: string;
  samplingTime: string;
  samplingAddress: AddressDto | null;
  samplingContact: AddressContactDto | null;
  wert1Result: string;
  wert2Result: string;
  overallResult: string;
  interpretation: CreateQuantiferonTestDtoInterpretation | null;
  file: File | null;
  notes: string;
  isTodo: boolean;
}

export function CreateQuantiferonResultForm({
  appointment,
  patientId,
  onClose,
  currentResult,
  currentResultId,
}: {
  appointment?: AppointmentDto | null;
  patientId: string;
  onClose: () => void;
  currentResult?: CreateQuantiferonTestDto;
  currentResultId?: string;
}) {
  const isEditing = !!currentResultId;

  const formMethods = useForm<FormValues>({
    defaultValues: getDefaultValues(appointment, currentResult),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty: isFormDirty },
  } = formMethods;

  const isSamplingAddressDisabled = !!currentResult?.samplingAddress;
  const isSamplingContactDisabled = !!currentResult?.samplingContact;
  const isFileInputDisabled = !!currentResult?.file;
  const isLmqtwert1 = !!currentResult?.qft1Result;
  const isLmqtwert2 = !!currentResult?.qft2Result;
  const isOverallResultRequired = !!currentResult?.overallResult;
  const isInterpretationRequired =
    !!currentResult?.interpretation && currentResult.interpretation !== CreateQuantiferonTestDtoInterpretation.PENDING;

  // debugger;
  const createQuantiferonTestMutation = useCreateQuantiferonTest({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetLabOrdersByPatientQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => toast.error('Beim speichern des Testresultates ist ein Fehler aufgetreten'),
    },
  });

  const updateQuantiferonTestMutation = useUpdateQuantiferonTest({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetLabOrdersByPatientQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => toast.error('Beim Aktualisieren des Testresultats ist ein Fehler aufgetreten'),
    },
  });

  const handleOnSubmit = async (data: FormValues) => {
    const createQuantiferonTestDto: CreateQuantiferonTestDto = {
      patientId: patientId,
      appointmentId: appointment?.id,
      file: data.file
        ? {
            fileName: data.file.name,
            file: await blobToBase64(data.file),
          }
        : undefined,
      interpretation: data.interpretation ?? CreateQuantiferonTestDtoInterpretation.PENDING,
      wert1Result: getStringOrUndefined(data.wert1Result),
      wert2Result: getStringOrUndefined(data.wert2Result),
      overallResult: getStringOrUndefined(data.overallResult),
      notes: getStringOrUndefined(data.notes),
      isTodo: data.isTodo,
      samplingDate: data.samplingDate,
      samplingTime: getStringOrUndefined(data.samplingTime),
      samplingAddress: data.samplingAddress ?? undefined,
      samplingContact: toLabOrderSamplingContact(data.samplingContact),
    };

    if (isEditing) {
      updateQuantiferonTestMutation.mutate({ id: currentResultId, data: createQuantiferonTestDto });
    } else {
      createQuantiferonTestMutation.mutate({ data: createQuantiferonTestDto });
    }
  };

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <Stack gap={2}>
          <Typography level={'title-lg'}>QuantiferonTBGold</Typography>
          <LabOrderDetailsForm
            samplingDate={'samplingDate'}
            samplingTime={'samplingTime'}
            samplingAddress={'samplingAddress'}
            samplingContact={'samplingContact'}
            file={'file'}
            isSamplingAddressDisabled={isSamplingAddressDisabled}
            isSamplingContactDisabled={isSamplingContactDisabled}
            fileInputName={'PDF Laborbefund'}
            fileInputPlaceHolder={currentResult?.file?.fileName ?? 'Laborbefund'}
            fileInputMimeType={'application/pdf'}
            isFileInputDisabled={isFileInputDisabled}
            isEditing={isEditing}
          />
          <Divider inset={'context'} />
          <Typography level={'title-lg'}>Resultat</Typography>
          <Stack direction={'row'} gap={2}>
            <FormControl sx={{ flex: 1 }} error={!!errors.wert1Result}>
              <FormLabel>
                <Typography>M.tbc Quantiferon® Wert 1 {isLmqtwert1 && '*'}</Typography>
              </FormLabel>
              <Input {...register('wert1Result', { required: isLmqtwert1 })} />
              {errors.wert1Result && <FormHelperText>M.tbc Quantiferon® Wert 1 muss ausgefüllt werden</FormHelperText>}
            </FormControl>
            <FormControl sx={{ flex: 1 }} error={!!errors.wert2Result}>
              <FormLabel>
                <Typography>M.tbc Quantiferon® Wert 2 {isLmqtwert2 && '*'}</Typography>
              </FormLabel>
              <Input {...register('wert2Result', { required: isLmqtwert2 })} />
              {errors.wert2Result && <FormHelperText>M.tbc Quantiferon® Wert 2 muss ausgefüllt werden</FormHelperText>}
            </FormControl>
          </Stack>
          <Stack direction={'row'} gap={2}>
            <FormControl sx={{ flex: 1 }} error={!!errors.overallResult}>
              <FormLabel>
                <Typography>Resultat Labor {isOverallResultRequired && '*'}</Typography>
              </FormLabel>
              <Input {...register('overallResult', { required: isOverallResultRequired })} />
              {errors.overallResult && <FormHelperText>Resultat Labor muss ausgefüllt werden</FormHelperText>}
            </FormControl>
            <FormControl sx={{ flex: 1 }} error={!!errors.interpretation}>
              <FormLabel>
                <Typography>Resultat {isInterpretationRequired && '*'}</Typography>
              </FormLabel>
              <Controller
                name={'interpretation'}
                control={control}
                rules={{ required: isInterpretationRequired }}
                render={({ field: { onChange, value } }) => (
                  <ResultInterpretationButtonGroup
                    value={value}
                    onChange={onChange}
                    resultOptions={[
                      CreateQuantiferonTestDtoInterpretation.POSITIVE,
                      CreateQuantiferonTestDtoInterpretation.NEGATIVE,
                      CreateQuantiferonTestDtoInterpretation.INDETERMINATE,
                    ]}
                  />
                )}
              />
              {errors.interpretation && <FormHelperText>Resultat muss angegeben werden</FormHelperText>}
            </FormControl>
          </Stack>
          <Divider inset={'context'} />
          <Typography level={'title-lg'}>Notiz</Typography>
          <Textarea {...register('notes')} minRows={2} maxRows={4} />
          {!isEditing && (
            <Controller
              name="isTodo"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Switch
                  checked={value}
                  onChange={onChange}
                  endDecorator={'Handlung erforderlich (ist ToDo)'}
                  sx={{ paddingTop: '1rem', alignSelf: 'flex-start' }}
                />
              )}
            />
          )}
          <FormActionButtons
            onAbortClick={handleOnClose}
            disableSubmit={!isFormDirty}
            isLoading={createQuantiferonTestMutation.isPending}>
            {currentResultId && (
              <DeleteLabOrderButton
                orderId={currentResultId}
                patientId={patientId}
                appointmentId={currentResult?.appointmentId}
                orderType={LabOrderDtoType.BLOOD_QUANTIFERON}
              />
            )}
          </FormActionButtons>
        </Stack>
      </form>
    </FormProvider>
  );
}

function getDefaultValues(appointment?: AppointmentDto | null, currentResult?: CreateQuantiferonTestDto): FormValues {
  return {
    samplingDate: getDefaultValueForDateInput(
      currentResult?.samplingDate,
      getAppointmentDate(appointment) ?? undefined,
    ),
    samplingTime: currentResult?.samplingTime
      ? getDefaultValueForLocalTimeInput(currentResult.samplingTime)
      : (getAppointmentTimeFrom(appointment) ?? ''),
    samplingAddress: currentResult?.samplingAddress ?? null,
    samplingContact: currentResult?.samplingContact ?? null,
    wert1Result: currentResult?.qft1Result as string,
    wert2Result: currentResult?.qft2Result as string,
    overallResult: currentResult?.overallResult ?? '',
    interpretation:
      currentResult?.interpretation === CreateQuantiferonTestDtoInterpretation.PENDING
        ? null
        : (currentResult?.interpretation ?? null),

    file: null,
    notes: currentResult?.notes ?? '',
    isTodo: currentResult?.isTodo ?? false,
  };
}
